'use client';

import type { SwiperOptions } from 'swiper/types';

import Image from 'next/image';
import React, { useRef } from 'react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';

export interface ImageData {
  alt: string;
  height: number;
  src: string;
  width: number;
}

interface ImageSlideshowProps {
  aspectRatio?: string;
  autoplay?:
    | {
        delay: number;
        disableOnInteraction?: boolean;
      }
    | false;
  breakpoints?: SwiperOptions['breakpoints'];
  centeredSlides?: boolean;
  className?: string;
  images: ImageData[];
  loop?: boolean;
  navigation?: boolean;
  onSlideChange?: () => void;
  onSwiper?: (swiper: any) => void;
  slideClassName?: string;
  slidesPerView?: number | 'auto';
  spaceBetween?: number;
  sizes?: string;
  speed?: number;
}

export const ImageSlideshow: React.FC<ImageSlideshowProps> = ({
  images,
  loop = true,
  navigation = false,
  centeredSlides = true,
  speed = 1000,
  autoplay = {
    delay: 1000,
    disableOnInteraction: false,
  },
  breakpoints = {
    640: {
      slidesPerView: 1,
      spaceBetween: 20,
    },
    768: {
      slidesPerView: 2,
      spaceBetween: 20,
    },
    1024: {
      slidesPerView: 3,
      spaceBetween: 20,
    },
  },
  className = '',
  aspectRatio = 'aspect-[16/10]',
  slideClassName,
  slidesPerView,
  spaceBetween,
  sizes,
  onSlideChange,
  onSwiper,
}) => {
  const swiperWrapperRef = useRef<HTMLElement | null>(null);

  const modules = [Autoplay];
  if (navigation) modules.push(Navigation);

  return (
    <section aria-label="Image carousel">
      <Swiper
        modules={modules}
        className={className}
        loop={loop}
        navigation={navigation}
        centeredSlides={centeredSlides}
        speed={speed}
        autoplay={autoplay}
        slidesPerView={slidesPerView}
        spaceBetween={spaceBetween}
        breakpoints={slidesPerView ? undefined : breakpoints}
        onSlideChange={onSlideChange}
        onSwiper={(swiper) => {
          swiperWrapperRef.current = swiper.wrapperEl;
          onSwiper?.(swiper);
        }}
      >
        {images.map((image, index) => (
          <SwiperSlide key={`${image.src}-${index}`} className={slidesPerView === 'auto' ? '!w-auto' : undefined}>
            <div className={slideClassName ?? `relative ${aspectRatio} w-full overflow-hidden`}>
              <Image src={image.src} alt={image.alt} fill sizes={sizes} className="object-cover" loading="lazy" />
              <div className="swiper-lazy-preloader swiper-lazy-preloader-white" />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
};
